<template>
  <div>
    <ClinicPageHeader
      title="Adicionar profissional de saúde"
      subtitle="Cadastre um novo profissional de saúde no corpo clínico"
    >
      <template #actions>
        <!-- Cadastro de médico é ação Admin (RBAC — Fase 2). -->
        <router-link
          :to="{ name: 'ClinicDoctors' }"
          class="btn btn--ghost btn--sm"
        >
          Cancelar
        </router-link>
      </template>
    </ClinicPageHeader>

    <div class="dbody" style="max-width:920px;margin:0 auto;width:100%">
      <!-- WIZARD STEPS -->
      <div
        v-if="currentStep !== 'done'"
        class="steps"
        style="margin-bottom:var(--space-8);max-width:680px"
      >
        <template v-for="(s, idx) in steps">
          <div :key="`step-${s.n}`" class="step" :class="stepClass(s.n)">
            <span class="step-dot">{{ s.n }}</span>
            <span class="step-label">{{ s.label }}</span>
          </div>
          <div
            v-if="idx < steps.length - 1"
            :key="`bar-${s.n}`"
            class="step-bar"
            :class="{ 'step-bar--done': stepNumber > s.n }"
          />
        </template>
      </div>

      <!-- STEP 1 — Dados do médico (só contato: CRM/UF/especialidade o
           próprio médico informa ao aceitar o convite) -->
      <div v-if="currentStep === 1">
        <div class="card">
          <fieldset class="fieldset">
            <span class="fieldset-title">Identificação</span>
            <p class="wiz-hint">
              O profissional de saúde recebe um convite por e-mail e completa o
              próprio cadastro (registro profissional, especialidade, documentos
              e agenda).
            </p>
            <div class="form-grid">
              <div class="field full">
                <label class="label">Nome completo</label>
                <input
                  v-model="form.name"
                  class="input"
                  :class="{ 'input--error': $v.form.name.$error }"
                  placeholder="Ex.: Dr. Lucas Ferraz"
                  @blur="$v.form.name.$touch()"
                />
                <span v-if="$v.form.name.$error" class="field-error">
                  Informe o nome completo do profissional de saúde.
                </span>
              </div>
              <div class="field">
                <label class="label">Telefone</label>
                <input
                  v-model="form.phone"
                  v-mask="['(##) ####-####', '(##) #####-####']"
                  type="tel"
                  class="input"
                  :class="{ 'input--error': $v.form.phone.$error }"
                  placeholder="(11) 90000-0000"
                  @blur="$v.form.phone.$touch()"
                />
                <span v-if="$v.form.phone.$error" class="field-error">
                  Informe um telefone com DDD.
                </span>
              </div>
              <div class="field">
                <label class="label">E-mail profissional</label>
                <input
                  v-model="form.email"
                  class="input"
                  :class="{ 'input--error': $v.form.email.$error }"
                  placeholder="medico@email.com"
                  @blur="$v.form.email.$touch()"
                />
                <span v-if="$v.form.email.$error" class="field-error">
                  Informe um e-mail válido.
                </span>
              </div>
            </div>
          </fieldset>
        </div>
        <div
          class="row"
          style="justify-content:flex-end;gap:var(--space-3);margin-top:var(--space-5)"
        >
          <router-link :to="{ name: 'ClinicDoctors' }" class="btn btn--ghost">
            Cancelar
          </router-link>
          <button class="btn btn--primary" @click="next">
            Continuar →
          </button>
        </div>
      </div>

      <!-- STEP 2 — Contrato & repasse -->
      <div v-else-if="currentStep === 2">
        <div class="card">
          <fieldset class="fieldset">
            <span class="fieldset-title">Modelo de repasse</span>
            <p class="wiz-hint">
              Percentual do valor da consulta repassado ao profissional de
              saúde. A clínica retém a diferença.
            </p>
            <ClinicSeg
              v-model="form.transferModel"
              :options="transferOptions"
              style="align-self:flex-start"
            />
            <div class="form-grid" style="margin-top:var(--space-4)">
              <div class="field">
                <label class="label">Repasse ao profissional de saúde</label>
                <input
                  v-model="customTransfer"
                  class="input"
                  :class="{ 'input--error': $v.customTransfer.$error }"
                  :readonly="form.transferModel !== 'custom'"
                  :placeholder="
                    form.transferModel === 'custom' ? 'Ex.: 65%' : ''
                  "
                  @blur="$v.customTransfer.$touch()"
                />
                <span v-if="$v.customTransfer.$error" class="field-error">
                  Informe um percentual entre 1% e 99%.
                </span>
              </div>
              <div class="field">
                <label class="label">Retenção da clínica</label>
                <input class="input" :value="clinicRetention" readonly />
              </div>
            </div>
          </fieldset>
          <hr class="wiz-sep" />
          <fieldset class="fieldset">
            <span class="fieldset-title">Atuação</span>
            <div class="form-grid">
              <div class="field">
                <label class="label">Unidade principal</label>
                <select v-model="form.unitId" class="select">
                  <option value="" disabled>Selecione…</option>
                  <option v-for="u in unitOptions" :key="u.id" :value="u.id">
                    {{ u.name }}
                  </option>
                </select>
              </div>
            </div>
          </fieldset>
        </div>
        <div
          class="row"
          style="justify-content:space-between;gap:var(--space-3);margin-top:var(--space-5)"
        >
          <button class="btn btn--outline" @click="goStep(1)">← Voltar</button>
          <button class="btn btn--primary" @click="next">
            Continuar →
          </button>
        </div>
      </div>

      <!-- STEP 3 — Revisão e envio do convite -->
      <div v-else-if="currentStep === 3">
        <div class="card">
          <fieldset class="fieldset">
            <span class="fieldset-title">Acesso à plataforma</span>
            <p class="wiz-hint">
              O convite é enviado para
              <b>{{ form.email }}</b>
              . O profissional de saúde cria a senha, informa o registro
              profissional e especialidade e completa o onboarding (documentos e
              agenda).
            </p>
          </fieldset>
          <hr class="wiz-sep" />
          <span class="fieldset-title">Resumo</span>
          <div class="dl" style="margin-top:var(--space-2)">
            <div>
              <dt>Profissional de saúde</dt>
              <dd>{{ form.name }}</dd>
            </div>
            <div>
              <dt>Telefone</dt>
              <dd>{{ form.phone }}</dd>
            </div>
            <div>
              <dt>E-mail</dt>
              <dd>{{ form.email }}</dd>
            </div>
            <div>
              <dt>Repasse</dt>
              <dd>
                {{ doctorTransfer }} profissional de saúde ·
                {{ clinicRetention }} clínica
              </dd>
            </div>
            <div>
              <dt>Unidade</dt>
              <dd>{{ selectedUnitName || "—" }}</dd>
            </div>
            <div>
              <dt>Status inicial</dt>
              <dd>Onboarding</dd>
            </div>
          </div>
        </div>
        <div
          class="row"
          style="justify-content:space-between;gap:var(--space-3);margin-top:var(--space-5)"
        >
          <button class="btn btn--outline" @click="goStep(2)">← Voltar</button>
          <button class="btn btn--primary" :disabled="sending" @click="finish">
            {{ sending ? "Enviando…" : "Enviar convite" }}
          </button>
        </div>
      </div>

      <!-- SUCESSO -->
      <div v-else-if="currentStep === 'done'">
        <div
          class="card"
          style="text-align:center;padding:var(--space-12) var(--space-8)"
        >
          <div
            class="empty-ill"
            style="background:var(--color-success-light);color:var(--color-success);width:88px;height:88px;margin:0 auto var(--space-4)"
          >
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
              <path
                d="M20 6L9 17l-5-5"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <h2
            style="font-size:var(--font-size-2xl);font-weight:var(--fw-extra);color:var(--color-gray-900)"
          >
            Profissional de saúde adicionado
          </h2>
          <p
            v-if="existingAccount"
            style="font-size:var(--font-size-md);color:var(--color-gray-500);max-width:440px;margin:var(--space-2) auto 0"
          >
            {{ existingAccountMessage }}
          </p>
          <p
            v-else
            style="font-size:var(--font-size-md);color:var(--color-gray-500);max-width:440px;margin:var(--space-2) auto 0"
          >
            Convite enviado para
            <b style="color:var(--color-gray-700)">{{ form.name }}</b>
            . Ele entra como
            <b style="color:var(--color-gray-700)">Onboarding</b>
            até concluir documentos e agenda.
          </p>
          <div
            class="row"
            style="gap:var(--space-3);justify-content:center;margin-top:var(--space-6)"
          >
            <router-link
              :to="{ name: 'ClinicDoctors' }"
              class="btn btn--primary"
            >
              Voltar para profissionais de saúde
            </router-link>
          </div>
          <button
            class="btn btn--ghost btn--sm"
            style="margin-top:var(--space-3)"
            @click="restart"
          >
            + Adicionar outro profissional de saúde
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { required, email } from "vuelidate/lib/validators";
import { mapGetters } from "vuex";
import { ClinicDoctors } from "@/services/clinica";
import ClinicPageHeader from "@/components/clinica/ClinicPageHeader.vue";
import ClinicSeg from "@/components/clinica/ClinicSeg.vue";

// Telefone com DDD: 10 (fixo) ou 11 (celular) dígitos.
const validPhone = value => {
  const digits = (value || "").replace(/\D/g, "");
  return digits.length === 10 || digits.length === 11;
};

export default {
  name: "ClinicDoctorNew",
  components: { ClinicPageHeader, ClinicSeg },
  data() {
    return {
      // Wizard: 1 → 2 → 3 → 'done'. Cada "Avançar" valida o passo via Vuelidate.
      currentStep: 1,
      steps: [
        { n: 1, label: "Dados do profissional de saúde" },
        { n: 2, label: "Contrato & repasse" },
        { n: 3, label: "Acesso" }
      ],
      transferOptions: [
        { value: "60", label: "60%" },
        { value: "70", label: "70%" },
        { value: "80", label: "80%" },
        { value: "custom", label: "Outro" }
      ],
      customTransfer: "60%",
      // Médico que já tem conta vê o convite no painel dele (além do e-mail).
      existingAccount: false,
      // Convite enxuto: a clínica informa contato + contrato. CRM/UF/
      // especialidade, documentos, conta bancária e agenda são do médico.
      form: {
        name: "",
        phone: "",
        email: "",
        transferModel: "60",
        unitId: ""
      },
      sending: false,
      // Médico criado ao concluir (para linkar o perfil na tela de sucesso).
      createdDoctor: null
    };
  },
  validations() {
    return {
      form: {
        name: { required },
        phone: { required, validPhone },
        email: { required, email }
      },
      // Repasse custom precisa ser um percentual válido (1–99).
      customTransfer: {
        validPct: value => {
          if (this.form.transferModel !== "custom") return true;
          const n = parseInt(value, 10);
          return !Number.isNaN(n) && n >= 1 && n <= 99;
        }
      }
    };
  },
  created() {
    // Clínica com uma unidade só: já vem selecionada.
    if (this.unitOptions.length === 1) {
      this.form.unitId = this.unitOptions[0].id;
    }
  },
  methods: {
    stepClass(n) {
      if (this.stepNumber > n || this.currentStep === "done") {
        return "step--done";
      }
      if (this.stepNumber === n) return "step--active";
      return "";
    },
    scrollTop() {
      // jsdom não implementa scrollTo — protege o ambiente de teste.
      try {
        if (typeof window !== "undefined" && window.scrollTo) {
          window.scrollTo(0, 0);
        }
      } catch (e) {
        /* noop */
      }
    },
    /** True se o passo n não tem campos inválidos. */
    stepValid(n) {
      const f = this.$v.form;
      if (n === 1)
        return !f.name.$invalid && !f.phone.$invalid && !f.email.$invalid;
      if (n === 2) return !this.$v.customTransfer.$invalid;
      if (n === 3) return this.stepValid(1) && this.stepValid(2);
      return true;
    },
    /** Marca como sujos apenas os campos do passo (para exibir os erros). */
    touchStep(n) {
      const f = this.$v.form;
      if (n === 1) {
        f.name.$touch();
        f.phone.$touch();
        f.email.$touch();
      } else if (n === 2) {
        this.$v.customTransfer.$touch();
      }
    },
    /** Avança para o próximo passo somente se o atual é válido. */
    next() {
      const current = this.currentStep;
      this.touchStep(current);
      if (!this.stepValid(current)) {
        this.$toast.error("Preencha os campos obrigatórios corretamente.");
        return;
      }
      this.goStep(current + 1);
    },
    goStep(n) {
      this.currentStep = n;
      this.scrollTop();
    },
    buildPayload() {
      return {
        name: this.form.name,
        phone: this.form.phone.replace(/\D/g, ""),
        email: this.form.email,
        transferPct: this.transferPct,
        unitId: this.form.unitId || null
      };
    },
    async finish() {
      if (this.sending) return;
      if (!this.stepValid(3)) {
        this.$toast.error("Revise os dados do convite antes de enviar.");
        return;
      }
      this.sending = true;
      try {
        const { data } = await ClinicDoctors.create(this.buildPayload());
        this.createdDoctor = data.doctor;
        this.existingAccount = !!data.existingAccount;
        this.currentStep = "done";
        this.$toast.success("Convite enviado");
        this.scrollTop();
      } catch (e) {
        // 422 da API vem como { error: "mensagem" } (ex.: unidade inválida).
        const apiError = e && e.response && e.response.data;
        this.$toast.error(
          (apiError && apiError.error) ||
            "Não foi possível adicionar o profissional de saúde."
        );
      } finally {
        this.sending = false;
      }
    },
    restart() {
      this.currentStep = 1;
      this.createdDoctor = null;
      this.form = {
        name: "",
        phone: "",
        email: "",
        transferModel: "60",
        unitId: this.unitOptions.length === 1 ? this.unitOptions[0].id : ""
      };
      this.$v.$reset();
      this.scrollTop();
    }
  },
  computed: {
    ...mapGetters("clinica", ["units"]),
    // Unidades reais da clínica (contexto de sessão) — não mais fixas.
    unitOptions() {
      return (this.units || []).filter(u => u && u.id && u.name);
    },
    selectedUnitName() {
      const unit = this.unitOptions.find(u => u.id === this.form.unitId);
      return unit ? unit.name : "";
    },
    existingAccountMessage() {
      return `${this.form.name} já tem conta na Videomedi. O convite aparece no painel dele(a) e também foi enviado por e-mail.`;
    },
    stepNumber() {
      return this.currentStep === "done" ? 4 : this.currentStep;
    },
    transferPct() {
      const model =
        this.form.transferModel === "custom"
          ? parseInt(this.customTransfer, 10)
          : Number(this.form.transferModel);
      return Number.isNaN(model) ? 70 : model;
    },
    doctorTransfer() {
      if (this.form.transferModel === "custom") {
        return this.customTransfer || "—";
      }
      return `${this.form.transferModel}%`;
    },
    clinicRetention() {
      if (this.form.transferModel === "custom") {
        const n = parseInt(this.customTransfer, 10);
        return Number.isNaN(n) ? "—" : `${100 - n}%`;
      }
      return `${100 - Number(this.form.transferModel)}%`;
    },
    createdId() {
      return this.createdDoctor ? String(this.createdDoctor.id) : "";
    }
  },
  watch: {
    "form.transferModel"(model) {
      if (model !== "custom") {
        this.customTransfer = `${model}%`;
      } else {
        this.customTransfer = "";
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.wiz-hint {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  margin: -4px 0 var(--space-2);
}

.wiz-sep {
  border: none;
  border-top: 1px solid var(--color-gray-100);
  margin: var(--space-6) 0;
}

.input--error {
  border-color: var(--color-danger);
}

.field-error {
  display: block;
  margin-top: 4px;
  font-size: var(--font-size-xs);
  color: var(--color-danger);
}
</style>
